<template>
  <div :class="sidebarClasses">
    <router-link
      :to="getLogoPath()"
      :style="{ 'pointer-events': isFraudFamilyCustomer || isDeactivatedFamilyCustomer ? 'none' : 'all' }"
    >
      <div class="logo">
        <a
          class="simple-text logo-mini"
          href="/"
        >
          <div class="logo-img">
            <img
              alt="gogo logo"
              :src="logoUrl"
            >
          </div>
        </a>
        <a
          class="simple-text logo-normal"
          href="/"
        >
          <div>{{ title }}</div>
          <div
            v-if="planDisplayName"
            class="plan-name"
          >
            <span>{{ planDisplayName }}</span>
            <br>
            <span>({{ planPrice }})</span>
          </div>
        </a>
      </div>
    </router-link>

    <announcements
      v-if="isOperatorDashboard && activeAnnouncements.length > 0"
      :announcements="activeAnnouncements"
    />

    <div
      ref="sidebarScrollAreas"
      class="sidebar-wrapper"
    >
      <slot />
      <form-search v-if="isOperatorDashboard" />

      <ul :class="navClasses">
        <slot name="links">
          <div v-if="isSidePanelVisible">
            <favorite-sidebar-items
              v-if="favoritePageItems.length > 0"
              :favorite-items="favoritePageItems"
            >
              <template #title>
                Favorite Pages
              </template>
            </favorite-sidebar-items>

            <favorite-sidebar-items
              v-if="favoriteFormItems.length > 0"
              :favorite-items="favoriteFormItems"
            >
              <template #title>
                Favorite Forms
              </template>
            </favorite-sidebar-items>

            <sidebar-item
              v-for="(link, index) in sidemenuItems"
              :key="link.name + index"
              :link="link"
              :index="index"
            />
          </div>

          <li class="sidemenu-item">
            <a href="#">
              <div
                class="logout"
                data-test="log-out"
                @click="logOut"
              >
                <i class="fa fa-power-off text-danger" />
                <p>Log Out</p>
              </div>
            </a>
          </li>
        </slot>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, toRefs, computed, watch, onMounted, defineAsyncComponent } from 'vue';
import queryString from 'query-string';

import util from '@/Utils/util';
import { useProfile } from '@/use/profile';
import { useDashboard } from '@/use/app';
import { useCustomer } from '@/use/customer';
import { usePartner } from '@/use/partner';
import { useGlobal } from '@/use/global';
import { sendPost } from '@/Utils/api';
import { FormListType, SidebarItemType } from '@/models/app';
import { specialPartnerCodes } from '@/constants/partner';
import { formList } from '@/Utils/formList';

const FormSearch = defineAsyncComponent(() => import('@/components/Main/Operator/FormSearch.vue') as any);

const Announcements = defineAsyncComponent(() => import('./Announcements.vue') as any);

const FavoriteSidebarItems = defineAsyncComponent(() => import('./FavoriteSidebarItems.vue') as any);

const logoUrl = '/static/img/gogo_logo.png';

const props = defineProps({
  title: {
    type: String,
    default: 'GoGoGrandparent',
  },
  type: {
    type: String,
    default: 'sidebar',
    validator: (value: string) => {
      let acceptedValues = ['sidebar', 'navbar'];
      return acceptedValues.indexOf(value) !== -1;
    },
  },
  backgroundColor: {
    type: String,
    default: 'black',
    validator: (value: string) => {
      let acceptedValues = ['white', 'brown', 'black'];
      return acceptedValues.indexOf(value) !== -1;
    },
  },
  activeColor: {
    type: String,
    default: 'success',
    validator: (value: string) => {
      let acceptedValues = ['primary', 'info', 'success', 'warning', 'danger'];
      return acceptedValues.indexOf(value) !== -1;
    },
  },
  logo: {
    type: String,
    default: '/static/img/gogo_logo.png',
  },
  sidebarLinks: {
    type: Array<any>,
    default: () => [],
  },
});

const originalSidebarItems: Ref<SidebarItemType[]> = ref([]);
const currentUserRoles: Ref<string[]> = ref([]);

const { currentUser, isFraudFamilyCustomer, isDeactivatedFamilyCustomer, setCurrentUser, favoritePages, favoriteForms } = useProfile();
const { dashboardCategory, isOperatorDashboard, isFamilyDashboard, isPartnerDashboard } = useDashboard();
const { customerPhoneNumber, customerPlans, excludedPages, setCustomerActivePlan } = useCustomer();
const { partnerInfo, partnerCode } = usePartner();
const { activeAnnouncements } = useGlobal();

const { planDisplayName, planPrice } = toRefs(customerPlans);

const sidebarClasses = computed(() => {
  if (props.type === 'sidebar') {
    return 'sidebar left-side-panel';
  }

  return 'collapse navbar-collapse off-canvas-sidebar';
});

const navClasses = computed(() => {
  if (props.type === 'sidebar') {
    return 'nav';
  }

  return 'nav navbar-nav';
});

const isSidePanelVisible = computed(() => {
  if (!currentUser.value?.id) {
    return false;
  }

  if (isFraudFamilyCustomer.value || isDeactivatedFamilyCustomer.value) {
    return false;
  }

  /*
    This is just defensive code.
    In general, if user has only one partner and the partner is disabled, the user can't login into system.
    This code is implemented for only validation to avoid unexpected issue if the API is changed to allow login for disabled partner.
  */
  if (isPartnerDashboard.value && !partnerInfo.value) {
    return false;
  }

  return true;
});

const sidemenuItems = computed(() =>
  originalSidebarItems.value
    .filter((link: any) => {
      return (link.path && !excludedPages.value.includes(link.path)) && (!link.children || link.children.length > 0);
    })
    .filter((item: any) => {
      if (isPartnerDashboard.value) {
        return !item.partnersToExclude?.includes(partnerCode?.value);
      } else {
        return item;
      }
    }),
);

const favoritePageItems = computed(() => {
  const sidebarItems: SidebarItemType[] = [];

  for (const favoritePage of favoritePages.value) {
    for (const sidebarItem of originalSidebarItems.value) {
      if (sidebarItem.children) {
        const favoriteSidebarItem = sidebarItem.children.find((item) => item.path === favoritePage);
        if (favoriteSidebarItem) {
          sidebarItems.push({
            name: favoriteSidebarItem.name,
            icon: sidebarItem.icon,
            path: favoriteSidebarItem.path,
          });

          break;
        }
      } else if (sidebarItem.path === favoritePage) {
        sidebarItems.push(sidebarItem);
      }
    }
  }

  return sidebarItems;
});

const favoriteFormItems = computed(() => {
  return favoriteForms.value
    .map((formId: string) => {
      const form = formList.find((item: FormListType) => item.id === formId);

      if (!form) {
        return null;
      }

      return {
        icon: form.icon,
        name: form.name,
        path: `${form.path}#${formId}`,
      };
    })
    .filter((item: SidebarItemType | null) => !!item);
});

const setSideBarItems = () => {
  originalSidebarItems.value = util.getSideMenuItems(dashboardCategory.value);
};

const logOut = async () => {
  try {
    await sendPost('logout');
  } catch (err) {
    // No need to do additional working
  } finally {
    localStorage.clear();
    window.location.href = '/login';
  }
};

const getLogoPath = () => {
  if (isFamilyDashboard.value) {
    return '/family/profile';
  }

  const queryParams = queryString.stringify({ phoneNumber: customerPhoneNumber.value });

  return customerPhoneNumber.value ? `/${dashboardCategory.value}/overview?${queryParams}` : `/${dashboardCategory.value}`;
};

// In partner dashboard, we show some menu items such as CallLog, Gourmet, etc for special partner,
// we need to update user roles for this feature
const checkSpecialPartnerCode = (currentPartnerCode: string, specialCodeName: string) => {
  const partnerCodeIndex = currentUserRoles.value.indexOf(specialCodeName);
  if (partnerCodeIndex > -1) {
    currentUserRoles.value.splice(partnerCodeIndex, 1);
  }

  if (currentPartnerCode === specialCodeName) {
    currentUserRoles.value.splice(0, 0, specialCodeName);
  }
};

watch(partnerInfo, (value: any) => {
  let partnerPlanIndex = currentUserRoles.value.indexOf('basic');
  if (partnerPlanIndex > -1) {
    currentUserRoles.value.splice(partnerPlanIndex, 1);
  }

  partnerPlanIndex = currentUserRoles.value.indexOf('premium');
  if (partnerPlanIndex > -1) {
    currentUserRoles.value.splice(partnerPlanIndex, 1);
  }

  if (value && value.plan) {
    currentUserRoles.value.splice(0, 0, value.plan);
  }

  // When partner is changed, we need to check and update the roles if partner is in special partners list.
  for (const specialPartnerCode of specialPartnerCodes) {
    checkSpecialPartnerCode(value?.codeName, specialPartnerCode);
  }

  localStorage.setItem('roles', JSON.stringify(currentUserRoles.value));

  setSideBarItems();

  setCurrentUser({
    ...currentUser.value,
    roles: currentUserRoles.value,
  });
});

watch(dashboardCategory, () => {
  // this is for when the operator clicks "Client Dashboard" and it loads the client dashboard onto the operator dashboard
  setCustomerActivePlan(null);
  setSideBarItems();
});

onMounted(() => {
  currentUserRoles.value = JSON.parse(localStorage.getItem('roles') || '[]');
  setSideBarItems();
});
</script>

<style lang="scss">
@media (min-width: 992px) {
  .navbar-search-form-mobile,
  .nav-mobile-menu {
    display: none;
  }
}
.sidebar-wrapper {
  overflow-y: auto;
}
.sidebar .nav li > a {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.sidebar .sidebar-wrapper > .nav li.active > a,
.off-canvas-sidebar .sidebar-wrapper > .nav li.active > a {
  color: #fff !important;
}
.sidebar .nav li:not(.active) > a,
.sidebar[data-background-color='white'] .nav li:not(.active) > a,
.off-canvas-sidebar .nav li:not(.active) > a,
.off-canvas-sidebar[data-background-color='white'] .nav li:not(.active) > a {
  color: #fff !important;
  text-transform: capitalize;
}
.sidebar.left-side-panel {
  display: flex;
  flex-direction: column;
  background-image: url('@/assets/img/bg-pattern.png');
  &:after {
    content: none;
  }
  &:before {
    z-index: -1;
  }
  .logo .simple-text .logo-img img {
    height: 35px !important;
    max-width: 35px !important;
    &.gold-logo {
      width: 34px !important;
      height: auto !important;
      border-radius: 30px;
    }
  }
  .sidemenu-item p {
    font-size: 14px;
  }
}
.sidebar .logo div.plan-name {
  margin-top: 7px;
  font-weight: bold;
  color: #fff700;
}
</style>
